import React from "react";
import { useNavigate } from "react-router-dom";
import { Modal, ModalSize } from "../Modal";
import { BtnSize, BtnType, Button } from "../Button";
import { Colors } from "../../Constants/colors";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";
import { Paths } from "../../Constants/paths";
import { StudentNavigationHeader } from "../../Constants/Enums/navigationHeader.enum";
import { UserAvatar, AvatarSize } from "../UserAvatar/UserAvatar";

interface Props {
  open: boolean;
  onClose: () => unknown;
  onProceedAnyway: () => unknown;
  studentName: string;
  existingStudentId: number | null;
}

const WarningIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2.5L1.5 21h21L12 2.5z"
      fill={Colors.TangerineOrange}
      fillOpacity={0.16}
      stroke={Colors.TangerineOrange}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M12 9.5v4.5"
      stroke={Colors.TangerineOrange}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="12" cy="17" r="1.1" fill={Colors.TangerineOrange} />
  </svg>
);

const LinkArrowIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12l-4.58 4.59z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Shown when a student with a matching name, class and branch is found
 * while filling in the admission form's second step. It surfaces the
 * name that was just entered, links straight to the existing student's
 * profile so office staff can double-check, and - since two different
 * students can share the same name - still lets the form advance if the
 * match is a false positive.
 */
export const StudentExistsModal = ({
  open,
  onClose,
  onProceedAnyway,
  studentName,
  existingStudentId,
}: Props) => {
  const navigate = useNavigate();

  const handleViewStudent = () => {
    if (!existingStudentId) return;
    onClose();
    navigate(
      `${Paths.StudentDetails}?section=${StudentNavigationHeader.STUDENT_INFO}&studentId=${existingStudentId}`,
    );
  };

  return (
    <Modal
      show={open}
      onClose={onClose}
      modalHeader="Student already exists"
      modalSize={ModalSize.sm}
      footerComponent={
        <div className="flex items-center justify-end gap-3">
          <Button
            text="Still proceed"
            btnType={BtnType.basic}
            btnSize={BtnSize.sm}
            onClick={onProceedAnyway}
          />
          <Button
            text="Cancel"
            btnType={BtnType.secondary}
            btnSize={BtnSize.sm}
            onClick={onClose}
          />
        </div>
      }
    >
      <div className="flex flex-col items-center gap-3">
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            backgroundColor: "#fff3e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <WarningIcon />
        </div>

        <div
          style={{
            fontFamily: FontFamily.ROBOTO_REGULAR,
            fontSize: "14px",
            color: Colors.DarkGrey,
            lineHeight: 1.5,
            textAlign: "center",
          }}
        >
          A student with the same name, in the same class and branch, is
          already registered in the system.
        </div>

        <div
          className="flex items-center gap-3 w-full"
          style={{
            border: `1.5px solid ${Colors.LightGrey}`,
            borderRadius: "10px",
            padding: "12px 14px",
            marginTop: "4px",
            boxSizing: "border-box",
          }}
        >
          <UserAvatar userName={studentName} size={AvatarSize.sm} image="" />
          <div style={{ textAlign: "left", overflow: "hidden", flex: 1 }}>
            <div
              style={{
                fontFamily: FontFamily.POPPINS_REGULAR,
                fontWeight: 600,
                fontSize: "14px",
                color: Colors.Black,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {studentName}
            </div>
            {existingStudentId ? (
              <div
                style={{
                  fontFamily: FontFamily.ROBOTO_REGULAR,
                  fontSize: "12px",
                  color: Colors.Grey,
                }}
              >
                Student ID: {existingStudentId}
              </div>
            ) : null}
          </div>

          {existingStudentId ? (
            <button
              type="button"
              onClick={handleViewStudent}
              className="flex items-center gap-1 cursor-pointer"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                flexShrink: 0,
                fontFamily: FontFamily.ROBOTO_REGULAR,
                fontSize: "12.5px",
                fontWeight: 600,
                color: Colors.TangerineOrange,
              }}
            >
              View details
              <LinkArrowIcon />
            </button>
          ) : null}
        </div>

        <div
          style={{
            fontFamily: FontFamily.ROBOTO_REGULAR,
            fontSize: "12.5px",
            color: Colors.Grey,
            textAlign: "center",
          }}
        >
          If this is a different student with the same name, you can still
          proceed. Otherwise, cancel and review the existing profile above.
        </div>
      </div>
    </Modal>
  );
};
